import type { AssignmentBuilderSchema, AssignmentEditSchema } from '@repo/schemas/assignment/assignmentBuilder';
import type { AssignmentFeedbackSchema, AssignmentSubmissionEditSchema, AssignmentSubmissionSchema } from '@repo/schemas/assignment/assignmentSubmission';
import type { GradeMappingSchema } from '@repo/schemas/grade-scale/gradeScale';
import type { AssignmentDetail, AssignmentListItem, AssignmentResponsePublic, AssignmentResponseWithDetails, PaginationWithAssignmentFilters } from '@repo/types/assignments';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';
const multipartHeaders = {
  headers: { 'Content-Type': 'multipart/form-data' },
};

const appendFormDataField = (form: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return;
  if (Array.isArray(value)) {
    value.forEach((v) => appendFormDataField(form, key, v));
    return;
  }
  if (typeof value === 'string') {
    form.append(key, value);
    return;
  }
  if (typeof value === 'number') {
    form.append(key, String(value));
    return;
  }
  if (typeof value === 'boolean') {
    form.append(key, value ? 'true' : 'false');
    return;
  }
  if (value instanceof Blob) {
    form.append(key, value);
  }
};

const buildFormData = (data: object, files: { field: string; files?: File[] }[] = []): FormData => {
  const form = new FormData();
  Object.entries(data).forEach(([key, value]) => appendFormDataField(form, key, value));
  files.forEach(({ field, files: list }) => {
    list?.forEach((file) => form.append(field, file));
  });
  return form;
};

export class AssignmentService {
  static async getAssignedAssignments(params?: PaginationWithAssignmentFilters): Promise<AxiosResponse<AssignmentListItem[]>> {
    return await axiosInstance.get('/api/assignment/assigned', { params });
  }

  static async getAssignment(id?: number): Promise<AxiosResponse<AssignmentDetailData>> {
    return await axiosInstance.get(`/api/assignment/${id}`);
  }

  static async createAssignment(input: CreateAssignmentInput): Promise<AxiosResponse<AssignmentDetail>> {
    const { data, attachments } = input;
    const { gradeMapping, ...rest } = data;
    const form = buildFormData(rest, [{ field: 'attachments', files: attachments }]);
    // gradeMapping is a nested object; multipart can't carry it, so send JSON
    // for the backend to parse. Omit when null so it defaults to no grading.
    if (gradeMapping != null) {
      form.append('gradeMapping', JSON.stringify(gradeMapping));
    }
    return await axiosInstance.post('/api/assignment', form, multipartHeaders);
  }

  static async editAssignment(input: EditAssignmentInput): Promise<AxiosResponse<AssignmentDetail>> {
    const { data, addFiles } = input;
    const { quizId, gradeMapping, ...rest } = data;
    const form = buildFormData(rest, [{ field: 'addFiles', files: addFiles }]);
    // `quizId: null` (detach) is dropped by appendFormDataField, and multipart
    // can't carry a real null. Send '' so the backend coerces it to null and
    // detaches the quiz; a uuid attaches it. Omit only when truly unchanged.
    if (quizId !== undefined) {
      form.append('quizId', quizId ?? '');
    }
    // gradeMapping is a nested object; multipart can't carry it, so send JSON
    // for the backend to parse, or '' to clear it. Omit only when unchanged.
    if (gradeMapping !== undefined) {
      form.append('gradeMapping', gradeMapping === null ? '' : JSON.stringify(gradeMapping));
    }
    return await axiosInstance.put('/api/assignment', form, multipartHeaders);
  }

  static async deleteAssignment(input: DeleteAssignmentInput): Promise<AxiosResponse<void>> {
    return await axiosInstance.delete('/api/assignment', {
      data: { id: input.id },
    });
  }

  static async getSubmissions(assignmentId?: number): Promise<AxiosResponse<AssignmentResponseWithDetails[]>> {
    return await axiosInstance.get(`/api/assignment/${assignmentId}/submit`);
  }

  static async submitAssignment(input: SubmitAssignmentInput): Promise<AxiosResponse<AssignmentResponsePublic>> {
    const { assignmentId, data, attachments } = input;
    const form = buildFormData(data, [{ field: 'attachments', files: attachments }]);
    return await axiosInstance.post(`/api/assignment/${assignmentId}/submit`, form, multipartHeaders);
  }

  static async editSubmission(input: EditAssignmentSubmissionInput): Promise<AxiosResponse<AssignmentResponsePublic>> {
    const { assignmentId, responseId, data, addFiles } = input;
    const form = buildFormData(data, [{ field: 'addFiles', files: addFiles }]);
    return await axiosInstance.put(`/api/assignment/${assignmentId}/submit/${responseId}`, form, multipartHeaders);
  }

  static async commentSubmission(input: CommentAssignmentSubmissionInput): Promise<AxiosResponse<AssignmentResponsePublic>> {
    const { assignmentId, responseId, feedback } = input;
    return await axiosInstance.put(`/api/assignment/${assignmentId}/submit/${responseId}/feedback`, { feedback });
  }
}

export interface AssignmentDetailData extends AssignmentDetail {
  gradeMapping: GradeMappingSchema | null;
}

export interface CreateAssignmentInput {
  data: AssignmentBuilderSchema;
  attachments?: File[];
}

export interface EditAssignmentInput {
  data: AssignmentEditSchema;
  addFiles?: File[];
}

export interface DeleteAssignmentInput {
  id: number;
}

export interface SubmitAssignmentInput {
  assignmentId: number;
  data: AssignmentSubmissionSchema;
  attachments?: File[];
}

export interface EditAssignmentSubmissionInput {
  assignmentId: number;
  responseId: number;
  data: AssignmentSubmissionEditSchema;
  addFiles?: File[];
}

export interface CommentAssignmentSubmissionInput {
  assignmentId: number;
  responseId: number;
  feedback: AssignmentFeedbackSchema;
}
